import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef, // custom code
} from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
/* custom code start: no lazy loaded moment, dates are formatted with Intl,
   the comments sit in the grid of the wiki view (#19379) */
import { Button, Comment, Container, Grid } from 'semantic-ui-react';
import CommentForm from '@horn-und-horn/volto-theme/components/manage/CommentForm';
/* custom code end */

import {
  addComment,
  deleteComment,
  listComments,
  listMoreComments,
} from '@plone/volto/actions/comments/comments';
import Avatar from '@plone/volto/components/theme/Avatar/Avatar';
import { CommentEditModal } from '@plone/volto/components/theme/Comments';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { getColor } from '@plone/volto/helpers/Utils/Utils';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';

const messages = defineMessages({
  comment: {
    id: 'Comment',
    defaultMessage: 'Comment',
  },
  comments: {
    id: 'Comments',
    defaultMessage: 'Comments',
  },
  commentDescription: {
    id: 'You can add a comment by filling out the form below. Plain text formatting.',
    defaultMessage:
      'You can add a comment by filling out the form below. Plain text formatting.',
  },
  default: {
    id: 'Default',
    defaultMessage: 'Default',
  },
  delete: {
    id: 'Delete',
    defaultMessage: 'Delete',
  },
  edit: {
    id: 'Edit',
    defaultMessage: 'Edit',
  },
  reply: {
    id: 'Reply',
    defaultMessage: 'Reply',
  },
  hideReplies: {
    id: 'Hide Replies',
    defaultMessage: 'Hide Replies',
  },
  showReplies: {
    id: 'Show Replies',
    defaultMessage: 'Show Replies',
  },
  loadMoreComments: {
    id: 'Load more',
    defaultMessage: 'Load more...',
  },
});

/* custom code start: no form schema, our CommentForm is a plain text area */
/* custom code end */

const useComments = () => {
  const items = useSelector((state) => state.comments.items, shallowEqual);
  const next = useSelector((state) => state.comments.next, shallowEqual);
  const items_total = useSelector(
    (state) => state.comments.items_total,
    shallowEqual,
  );
  const permissions = useSelector(
    (state) => state.comments.permissions || {},
    shallowEqual,
  );
  const addRequest = useSelector((state) => state.comments.add, shallowEqual);
  const deleteRequest = useSelector(
    (state) => state.comments.delete,
    shallowEqual,
  );

  return { items, next, items_total, permissions, addRequest, deleteRequest };
};

/* custom code start: German date and time of a comment (#19379) */
const formatDate = (date, options) =>
  new Intl.DateTimeFormat('de-DE', options).format(new Date(date));
/* custom code end */

const Comments = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname } = props;
  const [showEdit, setshowEdit] = useState(false);
  const [editId, seteditId] = useState(null);
  const [editText, seteditText] = useState(null);
  const [replyTo, setreplyTo] = useState(null);
  /* custom code start: replies cannot be collapsed in our layout (#19379) */
  /* custom code end */
  const { items, next, items_total, permissions, addRequest, deleteRequest } =
    useComments();

  const prevpathname = usePrevious(pathname);

  const prevaddRequestLoading = usePrevious(addRequest.loading);
  const prevdeleteRequestLoading = usePrevious(deleteRequest.loading);

  useEffect(() => {
    if (
      pathname !== prevpathname ||
      (prevaddRequestLoading && addRequest.loaded) ||
      (prevdeleteRequestLoading && deleteRequest.loaded)
    ) {
      dispatch(listComments(getBaseUrl(pathname)));
    }
  }, [
    dispatch,
    pathname,
    prevpathname,
    prevaddRequestLoading,
    addRequest.loaded,
    prevdeleteRequestLoading,
    deleteRequest.loaded,
  ]);

  /* custom code start: the delete action is laid out as a grid column whose
     width is taken from the rendered button (#19379) */
  const commentsWrapperRef = useRef(null);
  useEffect(() => {
    if (commentsWrapperRef.current == null) {
      return;
    }
    [...commentsWrapperRef.current.querySelectorAll('.comment')].forEach(
      (comment) => {
        const rect = comment.querySelector('.delete')?.getClientRects()[0];
        if (rect) {
          comment.style.setProperty('--delete-col', `${rect.width}px`);
        }
      },
    );
  }, [items]);
  /* custom code end */

  /* custom code start: our CommentForm hands over the plain text */
  const onSubmit = (comment) => {
    dispatch(addComment(getBaseUrl(pathname), comment, replyTo));
    setreplyTo(null);
  };
  /* custom code end */

  const setReplyTo = (commentId) => {
    setreplyTo(commentId);
  };

  const loadMoreComments = () => {
    dispatch(listMoreComments(flattenToAppURL(next)));
  };

  const onDelete = (value) => {
    dispatch(deleteComment(value));
  };

  const onEdit = useCallback((value) => {
    setshowEdit(true);
    seteditText(value.text);
    seteditId(value.id);
  }, []);

  const onEditOk = () => {
    setshowEdit(false);
    seteditId(null);
    seteditText(null);
    dispatch(listComments(getBaseUrl(pathname)));
  };

  const onEditCancel = useCallback(() => {
    setshowEdit(false);
    seteditId(null);
    seteditText(null);
    setreplyTo(null);
  }, []);

  const addRepliesAsChildrenToComments = (items) => {
    let initialValue = {};
    const allCommentsWithCildren = items.reduce((accumulator, item) => {
      return {
        [item.comment_id]: { comment: item, children: [] },
        ...accumulator,
      };
    }, initialValue);

    items.forEach((comment) => {
      if (comment.in_reply_to) {
        allCommentsWithCildren[comment.in_reply_to].children.push(comment);
      }
    });
    return allCommentsWithCildren;
  };

  const allCommentsWithCildren = useMemo(
    () => addRepliesAsChildrenToComments(items),
    [items],
  );
  // all comments that are not a reply will be shown in the first iteration
  const allPrimaryComments = items.filter((comment) => !comment.in_reply_to);

  // recursively makes comments with their replies nested
  // each iteration will show replies to the specific comment using allCommentsWithCildren
  /* custom code start: layout of the template, the comment is a css grid
     without Comment.Content, the avatar links to the user details, dates are
     German, the delete action has its own column, replies are not collapsible
     (#19379, #19371) */
  const commentElement = (comment) => (
    <Comment key={comment.comment_id} id={comment.comment_id}>
      <Avatar
        src={`/@portrait/${comment.author_username}`}
        title={comment.author_name || 'Anonymous'}
        color={getColor(comment.author_username)}
        userId={comment.author_username}
      />
      <Comment.Author>{comment.author_name}</Comment.Author>
      <Comment.Metadata>
        <span>
          {' '}
          <time
            dateTime={new Date(comment.creation_date).toISOString()}
            title={`${formatDate(comment.creation_date, {
              dateStyle: 'full',
              timeStyle: 'short',
            })} Uhr`}
          >
            <span>
              {formatDate(comment.creation_date, { dateStyle: 'short' })}
            </span>
            <span>
              {formatDate(comment.creation_date, { timeStyle: 'short' })} Uhr
            </span>
          </time>
        </span>
      </Comment.Metadata>
      <Comment.Text>
        {' '}
        {comment.text['mime-type'] === 'text/html' ? (
          <div
            dangerouslySetInnerHTML={{
              __html: comment.text.data,
            }}
          />
        ) : (
          comment.text.data
        )}
      </Comment.Text>
      <Comment.Actions className="delete">
        {comment.is_deletable && (
          <Comment.Action
            aria-label={intl.formatMessage(messages.delete)}
            onClick={() => onDelete(flattenToAppURL(comment['@id']))}
            color="red"
          >
            Löschen
          </Comment.Action>
        )}
      </Comment.Actions>
      <Comment.Actions>
        {comment.can_reply && (
          <Comment.Action
            as="a"
            aria-label={intl.formatMessage(messages.reply)}
            onClick={() => setReplyTo(comment.comment_id)}
            className={replyTo === comment.comment_id ? 'hide' : ''}
          >
            Antworten
          </Comment.Action>
        )}
        {comment.is_editable && (
          <Comment.Action
            onClick={() =>
              onEdit({
                id: flattenToAppURL(comment['@id']),
                text: comment.text.data,
              })
            }
            aria-label={intl.formatMessage(messages.edit)}
            value={{
              id: flattenToAppURL(comment['@id']),
              text: comment.text.data,
            }}
          >
            <FormattedMessage id="Edit" defaultMessage="Edit" />
          </Comment.Action>
        )}
      </Comment.Actions>
      <div id={`reply-place-${comment.comment_id}`}></div>

      {allCommentsWithCildren[comment.comment_id].children.length > 0 ? (
        <div className="replies">
          {allCommentsWithCildren[comment.comment_id].children.map(
            (child, index) => (
              <Comment.Group key={`group-${index}-${comment.comment_id}`}>
                {commentElement(child)}
              </Comment.Group>
            ),
          )}
        </div>
      ) : null}
    </Comment>
  );
  /* custom code end */

  if (!permissions.view_comments) return '';

  return (
    /* custom code start: grid of the wiki view, our CommentForm instead of
       the schema based form (#19379) */
    <Grid columns={3}>
      <Grid.Column computer={2} mobile={12} />
      <Grid.Column computer={8} mobile={12}>
        <div id="comments-content">
          <Container className="comments">
            <CommentEditModal
              open={showEdit}
              onCancel={onEditCancel}
              onOk={onEditOk}
              id={editId}
              text={editText}
            />
            {permissions.can_reply && (
              <div id="comment-add-id">
                <h2>Neuer Kommentar</h2>
                <CommentForm
                  onSubmit={onSubmit}
                  onCancel={onEditCancel}
                  resetAfterSubmit
                />
              </div>
            )}
            {/* all comments  */}
            <div
              className="ui comments"
              id="discussion"
              ref={commentsWrapperRef}
            >
              {allPrimaryComments.map((item) => commentElement(item))}
            </div>

            {/* load more button */}
            {items_total > items.length && (
              <Button fluid basic color="blue" onClick={loadMoreComments}>
                <FormattedMessage
                  id="Load more"
                  defaultMessage="Load more..."
                />
              </Button>
            )}

            {replyTo &&
              document &&
              createPortal(
                <CommentForm
                  onSubmit={onSubmit}
                  onCancel={onEditCancel}
                  resetAfterSubmit
                  canCancelOnEmpty
                  switchFocus
                />,
                document.getElementById(`reply-place-${replyTo}`),
              )}
          </Container>
        </div>
      </Grid.Column>
      <Grid.Column computer={2} mobile={12} />
    </Grid>
    /* custom code end */
  );
};

Comments.propTypes = {
  pathname: PropTypes.string.isRequired,
};

/* custom code start: no injectLazyLibs(['moment']) */
export default Comments;
/* custom code end */
